import { useEffect, useRef, useState } from 'react';

import './Pedido.css';

// Bibliotecas de terceiros
import { Box, Divider, TextField, Tooltip, ThemeProvider, Paper, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import InputMask from 'react-input-mask';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// Componentes personalizados
import CustomCard from '../../components/customCard/customCard';
import Loading from '../../components/loading/Loading';
import CustomTableItems from '../../components/customTableItems/CustomTableItems';
import CustomModalItems from '../../components/customModalItems/CustomModalItems';
import CustomModalSelectAgentes from '../../components/customModalSelectAgentes/CustomModalSelectAgentes';
import CustomModalSelectTabPrecos from '../../components/customModalSelectTabPreco/CustomModalSelectTabPreco';
import CustomModalSelectFormaCob from '../../components/customModalSelectFormaCob/CustomModalSelectFormaCob';
import CustomModalSelectPlanoPagamento from '../../components/customModalSelectPlanoPagamento/CustomModalSelectPlanoPagamento';
import Totais from '../../components/totais/totais';
import Observacao from '../../components/observacao/observacao';
import OutrasOpçoes from '../../components/outrasOpçoes/OutrasOpçoes';
import Informacoes from '../../components/informacoes/Informacoes';

// DTOs
import { DadosPedido, Pedido } from '../../dto/Pedidos';
import { Agente } from '../../dto/Agentes';
import { ItemPedido } from '../../dto/ItemPedido';
import { UserInfo } from '../../dto/UserInfo';
import { TabPreco } from '../../dto/TabPreco';
import { FormaCob } from '../../dto/FormaCob';
import { PlanoPagto } from '../../dto/PlanoPagamento';

// Serviços
import { PedidosServices } from '../../services/PedidosService';
import { ItemPedidoService } from '../../services/ItemPedidoService';

// Hooks
import { usePedido } from '../hooks/pedido';
import { useItemPedido } from '../hooks/itemPedido';
import { useStatusPedido } from '../hooks/statusPedido';

// Helpers
import { mostraMensagemErro, mostraMensagemSucesso } from '../../helpers/Notificacoes';
import BarraMenu from '../../components/barraMenu/BarraMenu';
import { ModoBarra } from '../../components/utils';
import { temaEdicao, temaVisualizacao } from '../../components/theme/Theme';
import { VerificaInativo } from '../../helpers/VerificaCampoInativo';
import ModalMensagem from '../../components/modal/Modal';


interface CadPedidoProps {
    modoInicial?: ModoBarra;
    pedidoSelecionado?: DadosPedido;
}

const CadPedido: React.FC<CadPedidoProps> = ({ modoInicial, pedidoSelecionado }) => {
    // fallback para deep clone (structuredClone quando disponível)
    const deepClone = <T,>(obj: T): T => {
        if (typeof structuredClone === 'function') return structuredClone(obj);
        return JSON.parse(JSON.stringify(obj));
    };

    // garante que um valor é número (0 como fallback)
    const toNumberSafe = (v: any): number => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    // formata número em BR com segurança (evita NaN)
    const formatBRSafe = (n: any) => {
        const num = toNumberSafe(n);
        return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Navegação / rota
    const navigate = useNavigate();
    const location = useLocation();

    // Services
    const pedidoService = new PedidosServices();

    // User info
    const [userInfo] = useState<UserInfo>(JSON.parse(localStorage.getItem('udbs')!));

    // Constantes fixas
    const DADOS_VAZIOS: DadosPedido = {
        idPedido: null,
        idEmpresa: userInfo.idEmpresa,
        idStatusPedido: '',
        nroPedido: null,
        dataLancamento: new Date(),
        idAgenteRepresentante: '',
        idAgente: '',
        idFormaCob: '',
        idPlanoPagto: '',
        idTabPreco: '',
        observacaoInterna: '',
        valorMercadoria: 0,
        valorIpi: 0,
        valorIcmsSubst: 0,
        valorDesconto: 0,
        valorFrete: 0,
        total: 0,
        observacaoRetorno: '',
        dataAtualizacao: new Date(),
        dataInfoFatEmissao: null,
        nroInfoFatDocumento: null,
        dataLancamentoFormated: '',
        agente: null,
        tabPreco: null,
        formaCob: null,
        planoPgto: null,
        itens: [],
        nroDocumentoPedido: null
    };

    // Estados principais do pedido
    const [pedidoOriginal, setPedidoOriginal] = useState<DadosPedido | null>(null);
    const [pedido, setPedido] = useState<DadosPedido>(pedidoSelecionado ?? DADOS_VAZIOS);

    // Estado de edição
    const [modoEdicao, setModoEdicao] = useState<ModoBarra>(modoInicial ?? ModoBarra.leitura);
    const [tema, setTema] = useState(temaVisualizacao)
    const isEditavel = modoEdicao === ModoBarra.edicaoregistro || modoEdicao === ModoBarra.novoregistro;
    const [statusEditavel, setStatusEditavel] = useState<boolean>();
    const [dadosEditados, setDadosEditados] = useState(false);
    const [mostrarOpcoesSalvar, setMostrarOpcoesSalvar] = useState(false);

    // Estado de agente
    const [agente, setAgente] = useState<Agente>();
    const [pedidoAgenteInformado, setpedidoAgenteInformado] = useState<boolean>(true);

    // helpers
    const formatBR = (n: number) =>
        n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const parseBR = (s: string) => {
        if (!s) return 0;
        // remove separadores de milhares e normaliza o decimal
        const normalized = s.replace(/\./g, "").replace(",", ".");
        const n = Number(normalized);
        return Number.isFinite(n) ? n : 0;
    };

    // Estado de interface
    const [openModal, setOpenModal] = useState(false);
    const [open, setOpen] = useState(false);
    const [dadosInvalidos, setDadosInvalidos] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Datas
    const [dataLancamento] = useState(() => {
        const dataAtual = new Date();
        return dataAtual.toLocaleString("pt-BR");
    });

    // Refs
    const tabelaAnteriorRef = useRef<TabPreco | null>(null);
    const itensPedidoInicial = useRef<ItemPedido[]>(pedido.itens).current;
    const carregouAgenteRef = useRef(false);

    // Listas / Itens
    const [deletedItems, setDeletedItems] = useState<ItemPedido[]>([]);

    // Hooks customizados
    const { itens, setItens, buscaItens } = useItemPedido();
    const [itensOriginal, setItensOriginal] = useState<ItemPedido[]>([]);

    const { agentes, buscouAgentes, agenteSelecionado, setAgenteSelecionado,
        tabPreco, buscouTabPreco, tabPrecoSelecionada, setTabPrecoSelecionada, agentePossuiTabPreco, setAgentePossuiTabPreco,
        formaCob, buscouFormaCob, formaCobSelecionada, setFormaCobSelecionada, agentePossuiFormaCob, setAgentePossuiFormaCob,
        planoPagamento, buscouPlanoPgto, planoPagamentoSelecionado, setPlanoPagamentoSelecionado, agentePossuiPlanoPgto, setAgentePossuiPlanoPgto,
        itensSelecionados, setItensSelecionados, itensPedidoCarregados,
        descricaoStatusPedido,
        ordemDigitacao,
        freteInput, setFreteInput,
        frete, setFrete,
        observacao, setObservacao,
        valorIpi, setValorIpi,
        valorIcmsSubst,
        handleSetItemPedido,
        totalItens, setTotalItens,
        totalPedido, setTotalPedido,
        dadosCarregados,
        carregaPedido,
    } = usePedido();

    const { selecionarStatusDoPedido, retornaOpcoesStatus } = useStatusPedido();

    const controleEdicao = () => {
        if (!pedidoSelecionado) return;
        const podeEditar = retornaOpcoesStatus(pedidoSelecionado.idStatusPedido)?.editavel;
        setStatusEditavel(podeEditar);
        if (!statusEditavel) {
            //Caso não possa editar, seta as variáveis de controle para o bloqueio de edição
            setAgentePossuiTabPreco(true);
            setAgentePossuiFormaCob(true);
            setAgentePossuiPlanoPgto(true);
        }
    };
    //busca os dados do pedido selecionado
    useEffect(() => {
        const buscaDados = async () => {
            if (modoInicial === 'novoregistro') {
                return;
            }
            setIsLoading(true);
            try {

                if (agentes.length === 0 || tabPreco.length === 0 || planoPagamento.length === 0 || formaCob.length === 0) {
                    return;
                }

                if (pedido && !dadosCarregados && !itensPedidoCarregados) {
                    await carregaPedido(pedido);
                    //await buscaItensPedidoItem(pedido.idPedido);
                }
            } finally {
                setIsLoading(false);
            }

            //setIsLoading(false);
        };
        controleEdicao();
        buscaDados();
    }, [dadosCarregados, itensPedidoCarregados, carregaPedido]);

    async function handleSetAgentePedido(agente: Agente) {

        if (agenteSelecionado && itensSelecionados.length) {
            mostraMensagemErro('Para alterar o agente cancele o pedido e refaça-o.')
            return
        }
        setAgente(agente);
        setAgenteSelecionado(agente);
        setDadosEditados(true);
        const trocaDeAgente = true;

        if (agente.idTabPreco) {
            const tabPrecoFiltered = tabPreco?.find((data: TabPreco) => data.idTabPreco === agente.idTabPreco);

            if (tabPrecoFiltered) {
                await handleSetTabPreco(tabPrecoFiltered, trocaDeAgente);
                setAgentePossuiTabPreco(true);

                buscaItens(tabPrecoFiltered.idTabPreco)
            }

        } else {
            setTabPrecoSelecionada(null);
            setItens(itens);
            setAgentePossuiTabPreco(false);
        }

        if (agente.idFormaCob) {
            const formaCobFiltered = formaCob?.find((data: FormaCob) => data.idFormaCob === agente.idFormaCob);

            if (formaCobFiltered) {
                await handleSetFormaCob(formaCobFiltered, trocaDeAgente);
                setAgentePossuiFormaCob(true);
            }

        } else {
            setFormaCobSelecionada(null);
            setAgentePossuiFormaCob(false);
        }

        if (agente.idPlanoPagto) {
            const planoPagtoFiltered = planoPagamento?.find((data: PlanoPagto) => data.idPlanoPagto === agente.idPlanoPagto);

            if (planoPagtoFiltered) {
                await handleSetPlanoPagamento(planoPagtoFiltered, trocaDeAgente);
                setAgentePossuiPlanoPgto(true);
            }

        } else {
            setPlanoPagamentoSelecionado(null);
            setAgentePossuiPlanoPgto(false);
        }
        carregouAgenteRef.current = true;

        setpedidoAgenteInformado(false);
    }

    useEffect(() => {
        if (carregouAgenteRef.current) {
            confereCamposAtivos();
            carregouAgenteRef.current = false;
        }
    });

    //monitora se a tabela de preços foi alterada
    useEffect(() => {
        tabelaAnteriorRef.current = null;
    }, [location.pathname]);

    useEffect(() => {
        buscaItens(tabPrecoSelecionada?.idTabPreco || null);
    }, [userInfo, tabPrecoSelecionada]);

    useEffect(() => {
        const buscaItensAtualizados = async () => {
            await buscaItens(tabPrecoSelecionada?.idTabPreco || null);
        }

        buscaItensAtualizados();
    }, [userInfo, tabPrecoSelecionada]);

    useEffect(() => {
        setTotalPedido(totalItens + valorIpi + frete + valorIcmsSubst)
        //console.log('=====>>>>>', totalItens, totalPedido)
    }, [totalItens, frete, valorIpi, valorIcmsSubst]);


    useEffect(() => {
        const atualizaPrecosTabela = async () => {
            if (!tabPrecoSelecionada || !isEditavel) return;

            const mudouTabela = tabelaAnteriorRef.current?.idTabPreco !== tabPrecoSelecionada.idTabPreco;

            if (mudouTabela) {

                const itensTabela: ItemPedido[] = await buscaItens(tabPrecoSelecionada.idTabPreco);
                // Recarrega todos os itens do zero
                const novosItens: ItemPedido[] = [];
                let total = 0;
                let totalIpi = 0;

                for (let itemPedido of itensSelecionados) {
                    const itemTabela = itensTabela.find(item => item.idItem === itemPedido.idItem);
                    if (!itemTabela) continue;

                    const ativo = itemTabela.ativo;
                    const valor = parseFloat(Number(itemTabela.valor).toFixed(4));
                    const qtd = Number(itemPedido.qtd);
                    const percIpi = Number(itemTabela.percIpi || 0);
                    const valorTotal = parseFloat((valor * qtd).toFixed(2));
                    const valorIpi = parseFloat((percIpi * (valorTotal / 100)).toFixed(2));

                    const item = {
                        ...itemPedido,
                        idEmpresa: userInfo!.idEmpresa,
                        descricao: itemTabela.descricao,
                        ativo,
                        valor,
                        qtd,
                        percIpi,
                        total: valorTotal,
                        valorIpi,
                        idItemPedido: itemPedido.idItemPedido || null,
                        idPedido: '',
                        idItem: itemPedido.idItem,
                        codUnidade: itemPedido.codUnidade,
                    };

                    total += valorTotal;
                    totalIpi += valorIpi;

                    novosItens.push(item);
                }

                setItensSelecionados(novosItens);
                setTotalItens(total);
                setValorIpi(totalIpi);

                tabelaAnteriorRef.current = tabPrecoSelecionada;
            }
        };

        atualizaPrecosTabela();
    }, [tabPrecoSelecionada, agentePossuiTabPreco]);


    async function handleDeleteItem(itemToRemove: ItemPedido) {

        const itemToDelete = itensSelecionados.find((item) =>
            item.codItem === itemToRemove.codItem &&
            item.ordemDigitacao === itemToRemove.ordemDigitacao
        );

        if (!itemToDelete) {
            mostraMensagemErro('Não foi possível remover o item. Item não encontrado.');
            return;
        }

        setTotalItens((prevTotal) => {
            if (itemToDelete.total === undefined) {
                return prevTotal;
            }

            const newTotal = parseFloat((Number(prevTotal || 0) - Number(itemToDelete.total || 0)).toFixed(2));
            return Math.max(newTotal, 0);
        });

        setValorIpi((prevIpi) => {
            if (itemToDelete.valorIpi === undefined) {
                return prevIpi;
            }

            const newTotal = parseFloat((Number(prevIpi || 0) - Number(itemToDelete.valorIpi || 0)).toFixed(2));
            return Math.max(newTotal, 0);
        });

        setItensSelecionados(prevItems =>
            prevItems.filter(item =>
                !(item.ordemDigitacao === itemToRemove.ordemDigitacao && item.idItem === itemToRemove.idItem)
            )
        );

        setDeletedItems(prevDeletedItems => [...prevDeletedItems, { ...itemToDelete }]);

        mostraMensagemSucesso('Item removido com sucesso');
    };

    async function handleSetTabPreco(tabPreco: TabPreco, trocaDeAgente?: boolean) {
        if (!userInfo) {
            mostraMensagemErro('Erro ao obter informações do usuário.');
            return;
        }

        if (agentePossuiTabPreco && !trocaDeAgente) {
            mostraMensagemErro('Agente selecionado já possui tabela de preço definida.');
            return;
        }

        if (!tabPreco) {
            mostraMensagemErro('Erro ao selecionar tabela de preço.');
            return;
        }

        tabPreco.idEmpresa = userInfo.idEmpresa;
        setTabPrecoSelecionada(tabPreco);
        setDadosEditados(true);
    }

    async function handleSetFormaCob(formaCob: FormaCob, trocaDeAgente?: boolean) {
        if (!userInfo) {
            mostraMensagemErro('Erro ao obter informações do usuário.');
            return;
        }

        if (agentePossuiFormaCob && !trocaDeAgente) {
            mostraMensagemErro('Agente selecionado já possui forma de cobrança definida.');
            return;
        }

        if (!formaCob) {
            mostraMensagemErro('Erro ao selecionar forma de cobrança.');
            return;
        }

        formaCob.idEmpresa = userInfo.idEmpresa;
        setFormaCobSelecionada(formaCob);
        setDadosEditados(true);
    }

    async function handleSetPlanoPagamento(planoPagamento: PlanoPagto, trocaDeAgente?: boolean) {
        if (!userInfo) {
            mostraMensagemErro('Erro ao obter informações do usuário.');
            return;
        }

        if (agentePossuiPlanoPgto && !trocaDeAgente) {
            mostraMensagemErro('Agente selecionado já possui plano de pagamento definido.');
            return;
        }

        if (!planoPagamento) {
            mostraMensagemErro('Erro ao selecionar plano de cobrança.');
            return;
        }

        planoPagamento.idEmpresa = userInfo.idEmpresa;
        setPlanoPagamentoSelecionado(planoPagamento);
        setDadosEditados(true);
    }

    function camposObrigatoriosSelecionados(idStatus: number) {

        /*  if (idStatus === 1) {
 
             if (itensSelecionados.length === 0) {
                 mostraMensagemErro('É necessário inserir ao menos um item no pedido.');
                 return false;
             }
             return true;
         } */

        if (itensSelecionados.length === 0) {
            mostraMensagemErro('É necessário inserir ao menos um item no pedido.');
            return false;
        }

        if (!formaCobSelecionada) {
            mostraMensagemErro('É necessário selecionar uma forma de cobrança.');
            return false;
        }

        if (!tabPrecoSelecionada) {
            mostraMensagemErro('É necessário selecionar uma tabela de preço.');
            return false;
        }

        if (!planoPagamentoSelecionado) {
            mostraMensagemErro('É necessário selecionar um plano de pagamento.');
            return false;
        }
        return true;
    };


    const handleClose = () => {
        setOpen(false);
        //setAction(0);
        setMostrarOpcoesSalvar(false)
    };

    const handleConfirm = () => {
        const camposValidos = confereCamposAtivos();
        if (!camposValidos) {
            // Interrompe o processo — não mostra opções de salvar
            return;
        }
        setMostrarOpcoesSalvar(true);
    };

    useEffect(() => {
    }, [mostrarOpcoesSalvar, tema]);

    function handleSaveObservacao(data: string) {
        setObservacao(data)
    };

    function updatePage(page: string, espera?: boolean) {

        if (espera) {
            setTimeout(() => {
                navigate("/mvp", {
                    state: {
                        propsPage: page
                    }
                });
            }, 3000);
        } else {
            navigate("/mvp", {
                state: {
                    propsPage: page
                }
            });
        }
    };

    function confereCamposAtivos() {
        const dadosInvalidos = [];

        const dadosComercial = VerificaInativo(
            [
                { nome: 'Plano de Pagamento', objeto: planoPagamentoSelecionado },
                { nome: 'Forma de Cobrança', objeto: formaCobSelecionada },
                { nome: 'Tabela de Preço', objeto: tabPrecoSelecionada }
            ]
        );
        if (dadosComercial.length) {
            dadosInvalidos.push({ titulo: 'Comercial', mensagem: dadosComercial })
        }

        const dadosAgente = VerificaInativo(
            [{ nome: 'Agente', objeto: agenteSelecionado }]
        )
        if (dadosAgente.length) {
            dadosInvalidos.push({ titulo: 'Agente', mensagem: dadosAgente })
        }

        let objItens: any = [];
        for (const item of itensSelecionados) {
            objItens.push({ nome: item.codItem, objeto: item })
        }

        const itensInativos = VerificaInativo(objItens);
        if (itensInativos.length) {
            dadosInvalidos.push({ titulo: 'Item', mensagem: itensInativos })
        }

        if (dadosInvalidos.length) {
            setDadosInvalidos(dadosInvalidos);
            setOpenModal(true);
            setIsLoading(false);
            return false;
        }

        return true;
    }


    const salvarPedido = async (statusPedido: number) => {
        setIsLoading(true);

        try {
            if (statusPedido > 0) {
                if (await !camposObrigatoriosSelecionados(statusPedido)) return;
                const statusSelecionado = selecionarStatusDoPedido(statusPedido);


                const itensPedidoEnviar: ItemPedido[] = itensSelecionados.map((data: ItemPedido) => ({
                    ...data,
                    idEmpresa: userInfo.idEmpresa,
                    ordemDigitacao: data.ordemDigitacao,
                    idItem: data.idItem,
                    qtd: data.qtd,
                    valor: data.valor,
                    valorIpi: data.valorIpi || 0,
                    valorIcmsSubst: 0,
                    valorDesconto: 0,
                    total: data.total,
                    codItem: data.codItem,
                    codUnidade: data.codUnidade
                }));

                const pedido: Pedido = {
                    idPedido: pedidoSelecionado?.idPedido!,
                    idEmpresa: userInfo.idEmpresa,
                    idStatusPedido: statusSelecionado!.idStatusPedido,
                    dataLancamento: pedidoSelecionado ? pedidoSelecionado.dataLancamento : new Date(),
                    idAgenteRepresentante: userInfo.idAgente,
                    idAgente: agenteSelecionado?.idAgente!,
                    idFormaCob: formaCobSelecionada?.idFormaCob,
                    idTabPreco: tabPrecoSelecionada?.idTabPreco,
                    idPlanoPagto: planoPagamentoSelecionado?.idPlanoPagto,
                    observacaoInterna: observacao,
                    valorMercadoria: parseFloat(totalItens.toFixed(2)),
                    valorIpi: 0, // parseFloat(valorIpi.toFixed(2)),
                    valorIcmsSubst: 0,
                    valorDesconto: 0,
                    valorFrete: parseFloat(frete.toFixed(2)),
                    total: parseFloat(totalPedido.toFixed(2)),
                    observacaoRetorno: '',
                    nroDocumentoPedido: pedidoSelecionado?.nroDocumentoPedido,
                    itens: itensPedidoEnviar,
                    itensParaDeletar: deletedItems
                };

                const pedidoSaved = await pedidoService.updatePedido(`/v1/pedido/app`, undefined, [pedido]);
                if (pedidoSaved.codigo !== 200) {
                    mostraMensagemErro(pedidoSaved.mensagem);
                    //SetEstadoAtual(pedido)
                    //return;
                }

                if (pedidoSaved.status) {
                    mostraMensagemSucesso('Operação realizada com sucesso.', 'pedido');
                    setPedido(pedido as DadosPedido);
                    setPedidoOriginal(pedido as DadosPedido);

                    if (modoInicial === ModoBarra.edicao) {
                        setModoEdicao(ModoBarra.edicao); // mostra botão "Editar"
                    } else {
                        setModoEdicao(ModoBarra.insercao); // mostra botão "Novo"
                    }

                    updatePage('pedido', true);


                } else {
                    mostraMensagemErro('Erro ao inserir itens do pedido.');
                    return;
                }

            } else {
                pedido.idStatusPedido = selecionarStatusDoPedido(statusPedido)?.idStatusPedido!;
                const resp = await pedidoService.cancelaPedido(`/v1/pedido/app`, undefined, [pedido]);
                console.log(resp)
                mostraMensagemSucesso('Pedido cancelado com sucesso.', 'pedido');
                updatePage('pedido', true);
            }

        } catch (error: any) {
            console.error('Erro ao salvar pedido:', error);
            mostraMensagemErro('Erro ao salvar pedido:' + error.erro);
            return;

        } finally {
            setIsLoading(false);
            setMostrarOpcoesSalvar(false)
            setPedidoOriginal(structuredClone(pedido)); // novo "original" após salvar
        }
    };

    const getDocumentType = (doc?: string) => {
        // Remove apenas pontuações (.), (-), (/) e espaços, mantendo letras e números
        const cleanDoc = doc?.replace(/[^a-zA-Z0-9]/g, '') || '';

        return cleanDoc.length <= 11 ? 'CPF' : 'CNPJ';
    };


    const getDocumentMask = (doc?: string) => {
        return getDocumentType(doc) === 'CPF' ? "999.999.999-99" : "**.***.***/****-99";
    };


    const getDocumentPlaceholder = (doc?: string) => {
        return getDocumentType(doc) === 'CPF' ? "___.___.___-__" : "__.___.___/____-__";
    };

    function voltar() {

        updatePage("pedido");
    };

    function EditarPedido() {
        //setPedidoOriginal(pedido); // guarda o estado original
        setPedidoOriginal(structuredClone(pedido));
        setItensOriginal(structuredClone(itens));
        setModoEdicao(ModoBarra.edicaoregistro);
        setTema(temaEdicao);
    }

    function NovoPedido() {
        setPedidoOriginal(null); // limpa estado original
        setPedido(DADOS_VAZIOS);
        setTabPrecoSelecionada(null);
        setFormaCobSelecionada(null);
        setPlanoPagamentoSelecionado(null);
        setModoEdicao(ModoBarra.novoregistro);
        setTema(temaEdicao);
    }

    function normalizaPedido(p: DadosPedido): DadosPedido {
        const pedido = deepClone(p);

        pedido.valorMercadoria = toNumberSafe(pedido.valorMercadoria);
        pedido.valorIpi = toNumberSafe(pedido.valorIpi);
        pedido.valorIcmsSubst = toNumberSafe(pedido.valorIcmsSubst);
        pedido.valorDesconto = toNumberSafe(pedido.valorDesconto);
        pedido.valorFrete = toNumberSafe(pedido.valorFrete);
        pedido.total = toNumberSafe(pedido.total);

        const itens = (pedido.itens || []).map(it => ({
            ...it,
            valor: toNumberSafe(it.valor),
            qtd: toNumberSafe(it.qtd),
            total: toNumberSafe(it.total),
            valorIpi: toNumberSafe(it.valorIpi)
        }));

        pedido.itens = itens;
        return pedido;
    }


    function Cancelar() {
        try {
            console.log('[Cancelar] modoEdicao=', modoEdicao);
            // caso esteja em edição/novo e exista snapshot original:
            if ((modoEdicao === ModoBarra.edicaoregistro || modoEdicao === ModoBarra.novoregistro) && pedidoOriginal) {

                // 1) restaura pedido e normaliza números
                const pedidoRestaurado = normalizaPedido(pedidoOriginal);

                // 2) garante nova referência de array para itens
                const itensRestaurados = (pedidoRestaurado.itens || []).map(i => ({ ...i }));
                pedidoRestaurado.itens = [...itensRestaurados];

                // 3) calcula totais a partir dos itens restaurados
                const totalItensCalc = itensRestaurados.reduce((s, it) => s + toNumberSafe(it.total), 0);
                const totalIpiCalc = itensRestaurados.reduce((s, it) => s + toNumberSafe(it.valorIpi), 0);
                const freteNum = toNumberSafe(pedidoRestaurado.valorFrete);
                const totalPedidoCalc = parseFloat((totalItensCalc + totalIpiCalc + freteNum).toFixed(2));

                // logs para debug — podes comentar depois
                console.log('[Cancelar] pedidoOriginal', pedidoOriginal);
                console.log('[Cancelar] itensRestaurados', itensRestaurados);
                console.log('[Cancelar] totais', { totalItensCalc, totalIpiCalc, freteNum, totalPedidoCalc });

                // 4) seta todos os states — assegura sincronização entre "pedido.itens" e os estados separados
                setPedido(pedidoRestaurado);
                setItensOriginal(deepClone(itensRestaurados)); // snapshot original dos itens
                setItensSelecionados(itensRestaurados);  // se usas este state para renderizar a tabela
                setDeletedItems([]);                      // limpa itens marcados para remoção

                // 5) atualiza os totais / valores relacionados
                setTotalItens(parseFloat(totalItensCalc.toFixed(2)));
                setValorIpi(parseFloat(totalIpiCalc.toFixed(2)));
                setFrete(freteNum);
                setFreteInput(formatBRSafe(freteNum));
                setTotalPedido(totalPedidoCalc);

                // 6) restaura seleções e observações
                setTabPrecoSelecionada(tabPreco.find(x => x.idTabPreco === pedidoRestaurado.idTabPreco) || null);
                setPlanoPagamentoSelecionado(planoPagamento.find(x => x.idPlanoPagto === pedidoRestaurado.idPlanoPagto) || null);
                setFormaCobSelecionada(formaCob.find(x => x.idFormaCob === pedidoRestaurado.idFormaCob) || null);

                setObservacao(pedidoRestaurado.observacaoInterna || '');

                // 7) volta ao modo leitura/edição conforme desejado
                setModoEdicao(ModoBarra.edicao);
                setTema(temaVisualizacao);

                // 8) (opcional) força remount da lista se a UI continuar "presa" (usar como último recurso)
                // setListKey(k => k + 1);
                return;
            }

            // caso seja cancelar novo registro — limpa tudo
            setPedido(deepClone(DADOS_VAZIOS));
            setItens([]);
            setItensOriginal([]);
            setItensSelecionados([]);
            setDeletedItems([]);
            setAgenteSelecionado(null);
            setTabPrecoSelecionada(null);
            setPlanoPagamentoSelecionado(null);
            setFormaCobSelecionada(null);
            setFreteInput('0,00');
            setFrete(0);
            setObservacao('');
            setTotalItens(0);
            setValorIpi(0);
            setTotalPedido(0);
            setModoEdicao(ModoBarra.leitura);

        } catch (err) {
            console.error('[Cancelar] erro:', err);
            // Mostra uma mensagem ao usuário se necessário
            mostraMensagemErro('Erro ao restaurar o pedido. Verifica console.');
        }
    }


    return (
        <div className="paginaCadastro">
            <ThemeProvider theme={tema}>
                <Box className='containerFormCadastro'>
                    <Paper elevation={0} className='pedido-paper-header'>
                        <Box className='pedido-cad-header-row'>
                            <Box className='pedido-cad-title-wrap'>
                                <Box className='pedido-cad-title-icon'>
                                    <ReceiptLongIcon />
                                </Box>
                                <Box>
                                    <Typography className='pedido-cad-title'>
                                        Cadastro de Pedido
                                    </Typography>
                                    <Typography className='pedido-cad-subtitle'>
                                        Gerencie as informações e dados do pedido cadastrado.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className='pedido-cad-actions'>
                                <BarraMenu
                                    pTitulo={
                                        pedido?.nroPedido
                                            ? `Pedido - ${pedido.nroPedido}`
                                            : "Novo Pedido"
                                    }
                                    //modoInicial={modoEdicao} // valor controlado
                                    modoAtual={modoEdicao}
                                    onAcao={(acao) => {
                                        if (acao === "edicaoregistro") EditarPedido();
                                        if (acao === "cancelar" || acao === "voltar") Cancelar();
                                        if (acao === "novoregistro") NovoPedido();
                                        if (acao === "sair") voltar();
                                        if (acao === "salvar") handleConfirm();
                                    }}
                                    pEditavel={statusEditavel!}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {mostrarOpcoesSalvar && (
                        <OutrasOpçoes
                            editavel={isEditavel}
                            salvarPedido={salvarPedido}
                            fechar={handleClose}
                        />
                    )}

                    <Box className='pedido-form-card'>
                        {isLoading && <Loading />}
                        <Box>
                            <Box sx={{ maxWidth: 1020, display: 'flex', flexWrap: 'wrap', gap: 2, paddingBottom: 2 }}>

                                <TextField
                                    className="fields"
                                    id="pedidoodin"
                                    label="Ped. Odin"
                                    value={pedido?.nroDocumentoPedido}
                                    variant="outlined"
                                    sx={{ width: 100 }}
                                    InputLabelProps={{ shrink: true }}
                                    disabled
                                    size='small'
                                />

                                <InputMask mask="99/99/9999 99:99" placeholder="__/__/____ __:__" value={new Date(pedido?.dataLancamento!).toLocaleString("pt-BR") || dataLancamento} disabled>
                                    {() => (
                                        <TextField
                                            className="fields"
                                            id="data-lancamento"
                                            label="Data lançamento"
                                            placeholder="__/__/____"
                                            variant="outlined"
                                            sx={{ width: 165 }}
                                            InputLabelProps={{ shrink: true }}
                                            size='small'
                                            disabled
                                        />
                                    )}
                                </InputMask>

                                <TextField
                                    className="fields"
                                    id="cod-agente"
                                    label="Cod Agente"
                                    value={agenteSelecionado?.codAgente || ''}
                                    variant="outlined"
                                    sx={{ width: 100 }}
                                    disabled
                                    InputLabelProps={{ shrink: !!agenteSelecionado?.codAgente, sx: { fontSize: '13px' } }}
                                    size='small'
                                />

                                <Box sx={{ position: 'relative', width: 230 }}>
                                    <InputMask
                                        mask={getDocumentMask(agenteSelecionado?.cnpjCpf)}
                                        placeholder={getDocumentPlaceholder(agenteSelecionado?.cnpjCpf)}
                                        value={agenteSelecionado?.cnpjCpf || ""}
                                        disabled={pedidoAgenteInformado}
                                    >
                                        {() => (
                                            <TextField
                                                className="fields"
                                                id="cnpj-cpf"
                                                label={('CPF/CNPJ')}
                                                placeholder={getDocumentPlaceholder(agenteSelecionado?.cnpjCpf)}
                                                variant="outlined"
                                                sx={{ width: '100%' }}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: '13px' } }}
                                                size='small'
                                                disabled={pedidoAgenteInformado}
                                            />
                                        )}
                                    </InputMask>

                                    <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
                                        <CustomModalSelectAgentes
                                            data={agentes}
                                            buscouAgentes={buscouAgentes}
                                            openModal={false}
                                            handleSetAgentePedido={handleSetAgentePedido}
                                            isSearch={true}
                                            podeEditar={modoInicial === "novoregistro" ? isEditavel : false}
                                            handleFunction={() => { }}
                                            handleEditAgente={() => { }}
                                        />
                                    </Box>
                                </Box>

                                <TextField
                                    className="fields"
                                    id="razao-social"
                                    label="Razão Social"
                                    value={agenteSelecionado?.nome || ''}
                                    variant="outlined"
                                    sx={{ width: 360 }}
                                    disabled
                                    InputLabelProps={{ shrink: !!agenteSelecionado?.nome, sx: { fontSize: '14px' } }}
                                    size='small'
                                />
                            </Box>

                          {/*   <Divider sx={{ maxWidth: 1020, marginY: 2 }} /> */}

                            <Box sx={{ maxWidth: 1020, display: 'flex', flexWrap: 'wrap', gap: 2 }}>

                                <Box sx={{ position: 'relative', minWidth: 275 }}>
                                    <Tooltip
                                        title={tabPrecoSelecionada ? tabPrecoSelecionada.descricao : ""}
                                    >
                                        <TextField
                                            className="fields"
                                            id="tabela-preco"
                                            label="Tabela preço"
                                            value={tabPrecoSelecionada ? tabPrecoSelecionada.descricao : ""}
                                            variant="outlined"
                                            sx={{ width: "100%" }}
                                            disabled
                                            InputLabelProps={{
                                                shrink: tabPrecoSelecionada ? !!tabPrecoSelecionada.descricao : undefined,
                                                sx: { fontSize: "14px" }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                sx: {
                                                    input: {
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        paddingRight: "30px",
                                                        // 👉 aplica estilo condicional baseado no ativo
                                                        textDecoration:
                                                            tabPrecoSelecionada?.ativo === "N" ? "line-through" : "none",
                                                        opacity: tabPrecoSelecionada?.ativo === "N" ? 0.5 : 1,
                                                        fontStyle:
                                                            tabPrecoSelecionada?.ativo === "N" ? "italic" : "normal"
                                                    }
                                                }
                                            }}
                                            size="small"
                                        />
                                    </Tooltip>

                                    <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: agenteSelecionado ? 'block' : 'none' }}>
                                        <CustomModalSelectTabPrecos
                                            data={tabPreco}
                                            buscouTabelas={buscouTabPreco}
                                            openModal={false}
                                            isSearch={true}
                                            handleFunction={() => { }}
                                            handleSetTabPreco={handleSetTabPreco}
                                            haveTabPreco={agentePossuiTabPreco ? agentePossuiTabPreco : !isEditavel}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ position: 'relative', width: 275 }}>
                                    <Tooltip title={
                                        planoPagamentoSelecionado
                                            ? `${planoPagamentoSelecionado.descricao}`
                                            : ''}>
                                        <TextField
                                            className="fields"
                                            id="cond-pagamento"
                                            label="Cond. pagamento"
                                            value={
                                                planoPagamentoSelecionado
                                                    ? `${planoPagamentoSelecionado.descricao}`
                                                    : ''
                                            }
                                            variant="outlined"
                                            sx={{ width: '100%' }}
                                            disabled
                                            InputLabelProps={{
                                                shrink: planoPagamentoSelecionado ? !!planoPagamentoSelecionado.descricao : undefined,
                                                sx: { fontSize: '14px' }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                sx: {
                                                    input: {
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        paddingRight: '30px',
                                                        textDecoration:
                                                            planoPagamentoSelecionado?.ativo === "N" ? "line-through" : "none",
                                                        opacity: planoPagamentoSelecionado?.ativo === "N" ? 0.5 : 1,
                                                        fontStyle:
                                                            planoPagamentoSelecionado?.ativo === "N" ? "italic" : "normal"
                                                    },
                                                },
                                            }}
                                            size='small'
                                        />
                                    </Tooltip>
                                    <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: agenteSelecionado ? 'block' : 'none' }}>
                                        <CustomModalSelectPlanoPagamento
                                            data={planoPagamento}
                                            buscouPlanoPgto={buscouPlanoPgto}
                                            openModal={false}
                                            isSearch={true}
                                            handleFunction={() => { }}
                                            handleSetPlanoPagamento={handleSetPlanoPagamento}
                                            havePlanoPgto={agentePossuiPlanoPgto ? agentePossuiPlanoPgto : !isEditavel}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ position: 'relative', width: 275 }}>
                                    <Tooltip title={
                                        formaCobSelecionada
                                            ? `${formaCobSelecionada.descricao}`
                                            : ''}>
                                        <TextField
                                            className="fields"
                                            id="forma-cobranca"
                                            label="Forma cobrança"
                                            value={
                                                formaCobSelecionada
                                                    ? `${formaCobSelecionada.descricao}`
                                                    : ''
                                            }
                                            variant="outlined"
                                            sx={{ width: '100%' }}
                                            disabled
                                            InputLabelProps={{
                                                shrink: formaCobSelecionada ? !!formaCobSelecionada.descricao : undefined,
                                                sx: { fontSize: '14px' }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                sx: {
                                                    input: {
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        paddingRight: '30px',
                                                        textDecoration:
                                                            formaCobSelecionada?.ativo === "N" ? "line-through" : "none",
                                                        opacity: formaCobSelecionada?.ativo === "N" ? 0.5 : 1,
                                                        fontStyle:
                                                            formaCobSelecionada?.ativo === "N" ? "italic" : "normal"
                                                    },
                                                },
                                            }}
                                            size='small'
                                        />
                                    </Tooltip>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 1,
                                            backgroundColor: 'white', display: agenteSelecionado ? 'block' : 'none'
                                        }}>
                                        <CustomModalSelectFormaCob
                                            data={formaCob}
                                            buscouFormasCob={buscouFormaCob}
                                            openModal={false}
                                            isSearch={true}
                                            handleFunction={() => { }}
                                            handleSetFormaCob={handleSetFormaCob}
                                            haveAgente={agentePossuiFormaCob ? agentePossuiFormaCob : !isEditavel}
                                        />
                                    </Box>
                                </Box>

                                <TextField
                                    className="fields"
                                    id="frete"
                                    label="Frete"
                                    value={freteInput}
                                    onChange={(e) => {
                                        let v = e.target.value;

                                        // permite apenas dígitos, vírgula e ponto
                                        v = v.replace(/[^0-9,\.]/g, "");

                                        // se o utilizador usar ponto como decimal, converte para vírgula
                                        // (mas remove todos os pontos de milhares)
                                        v = v.replace(/\./g, "");           // remove milhares
                                        const parts = v.split(",");          // garante só uma vírgula
                                        if (parts.length > 2) v = parts[0] + "," + parts.slice(1).join("").replace(/,/g, "");

                                        // atualiza a string visível
                                        setFreteInput(v);

                                        // atualiza o número para cálculos (sempre!)
                                        setFrete(parseBR(v));
                                    }}
                                    onFocus={() => {
                                        // ao focar para editar, mostra sem separador de milhares (ex.: "5000,65")
                                        setFreteInput((prev) => (prev ? prev.replace(/\./g, "") : (frete ? String(formatBR(frete)) : "").replace(/\./g, "")));
                                    }}
                                    onBlur={() => setFreteInput(formatBR(frete))}
                                    variant="outlined"
                                    sx={{ width: 145 }}
                                    size="small"
                                    InputLabelProps={{
                                        sx: { fontSize: '14px', color: "#4d4d4d" }
                                    }}
                                    disabled={!isEditavel}
                                />

                            </Box>
                        </Box>

                        <Divider sx={{ maxWidth: 1000, marginY: 2}} />

                        <div className="Buttom">
                            <CustomModalItems
                                data={itens}
                                openModal={openModal}
                                deleteData={undefined}
                                isSearch={false}
                                handleFunction={() => { }}
                                setItemPedido={handleSetItemPedido}
                                editavel={isEditavel}
                                ordemDigitacao={ordemDigitacao}
                                camposSelecionados={!agenteSelecionado ? "Selecione o agente" : null}
                                temTabPreco={tabPrecoSelecionada !== null}
                            />

                        </div>
                        <div className="TableItems">
                            <CustomTableItems
                                data={itensSelecionados}
                                handleSetItemPedido={handleSetItemPedido}
                                isPedido={true}
                                search={false}
                                handleDeleteItem={handleDeleteItem}
                                editavel={isEditavel}
                                temTabPreco={tabPrecoSelecionada !== null}
                            />
                        </div>
                        <div className='ContextInfoPedido'>
                            <Box sx={{
                                display: 'flex',
                                gap: 3,
                                '& .card-title': {
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    marginBottom: 1,
                                    width: '100%',
                                    textAlign: 'left'
                                }
                            }}> <div>
                                    <CustomCard width={1020} height={80} maxWidth={200} color={"#334155"} margin={0} marginTop={2}>
                                        <Informacoes
                                            status={descricaoStatusPedido || ''}
                                            nf={pedido?.nroInfoFatDocumento?.toString() || ''}
                                            datalancamento={
                                                pedido?.dataInfoFatEmissao
                                                    ? new Date(pedido.dataInfoFatEmissao).toLocaleString("pt-BR")
                                                    : "" // 🔑 se for nulo ou undefined, mostra vazio
                                            }
                                        />
                                    </CustomCard>

                                </div></Box>
                        </div>
                        <div className="ContextTotal">
                            <Box sx={{
                                display: 'flex',
                                gap: 3,
                                '& .card-title': {
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    marginBottom: 2,
                                    width: '100%',
                                    textAlign: 'left'
                                }
                            }}>
                                <div>
                                    <CustomCard width={660} height={180} maxWidth={200} color={"#0000000A"} margin={0}>
                                        <Observacao
                                            observacaoInterna={pedido?.observacaoInterna || ''}
                                            retorno={pedido?.observacaoRetorno || ''}
                                            handleSetObservacaoInterna={handleSaveObservacao}
                                            editavel={isEditavel}
                                        />
                                    </CustomCard>
                                </div>

                                <div>
                                    <CustomCard width={330} height={180} maxWidth={200} color={"#0000000A"} margin={1}>
                                        <Totais
                                            ipi={valorIpi}
                                            st={pedido.valorIcmsSubst! ? pedido.valorIcmsSubst : 0}
                                            ii={0}
                                            totalItens={totalItens}
                                            frete={frete}
                                            totalPedido={totalPedido}
                                        />
                                    </CustomCard>
                                </div>
                            </Box>
                            <ModalMensagem
                                mensagem='Validação do Pedido'
                                open={openModal}
                                onClose={() => setOpenModal(false)}
                                dados={dadosInvalidos}
                            />
                            <ToastContainer />
                        </div>
                    </Box>
                </Box>

            </ThemeProvider>
        </div>
    );
}

export default CadPedido;
